/**
 * What this browser has already been shown about its clients.
 *
 * Two different things are remembered here, and they are kept in one set on
 * purpose: both are ids the backend minted, both are unique across the whole
 * workspace, and both answer the same question — has the practitioner in front
 * of this browser laid eyes on it?
 *
 *   - a client's own `public_id`, put in when their detail screen loads. Until
 *     then the row wears a "New" tag;
 *   - a pre-consultation form's `public_id`, put in when the client it belongs
 *     to is opened with the answers sitting there. Until then the row wears a
 *     dot and the rail counts it.
 *
 * The form is remembered by its own id rather than by the client's, which is
 * the whole reason there are two: a client opened last week and sent a form
 * that came back this morning is news again, and a set keyed by the client
 * alone could not say so.
 *
 * See `openedRecords` for the machinery, and `clientNews` for the reading of
 * these ids against a record.
 */

import { createOpenedRecordsStore } from "../../lib/openedRecords";

/** Single key, holding the ids in the order they were first seen. */
const STORAGE_KEY = "naurist:opened-clients";

const store = createOpenedRecordsStore(STORAGE_KEY);

/** Subscribes to every change made here, and returns the unsubscribe. */
export const subscribeToOpenedClients = store.subscribe;

/**
 * The ids seen on this device, as a set to test a row against. Read from
 * storage on the first call rather than in an effect, so a reloaded list draws
 * its flags in the first paint instead of flashing them on and off.
 */
export const getOpenedClients = store.get;

/**
 * Marks one client as opened — what takes the "New" tag off their row and
 * their one off the rail's count. `ClientDetailPage` calls it.
 */
export const markClientOpened = store.mark;

/**
 * Marks a pre-consultation submission as seen, by every key it answers to.
 *
 * Every one of them, because the screen that calls this holds the client's
 * whole record while the list that asks holds a copy that may be trimmed — so
 * the key the list will ask about is not known here, and all of them are
 * cheap. See `submissionKeys`.
 *
 * Only for a form that has come back: a link still out has nothing to have
 * looked at, and remembering it would silence the answers when they arrive.
 */
export const markPreconsultationSeen = (keys: readonly string[]): void => {
  keys.forEach((key) => store.mark(key));
};

/** Where the baseline moment is kept — see `clientsNewsSince`. */
const SINCE_KEY = "naurist:clients-news-since";

/** Read once and held, so every reader of it agrees within a session. */
let since: number | undefined;

/**
 * The moment this browser started counting — clients on the books before it
 * are not news, however long ago anybody last opened them.
 *
 * Without a line like this, a practice with sixty clients meets this feature
 * as a badge reading sixty. Every one of those is true and none of them is
 * news, and a badge that has to be dismissed sixty times to become useful will
 * simply be ignored instead.
 *
 * A moment rather than a baseline list of ids, because a list has to be
 * complete to be right: it would have to be laid down from the whole roster,
 * and the rail reads one page of it. One stored timestamp says the same thing
 * about a workspace of six clients and one of six hundred.
 *
 * Set the first time it is asked for, which is the first render of the rail
 * after this browser met the feature. A browser that refuses storage gets the
 * moment it loaded the app instead — so a session still counts arrivals
 * correctly, it just starts counting again on the next reload, which is the
 * quiet failure rather than the loud one.
 *
 * Note this is per browser and not per account. The ids beside it are the
 * same, and for the same reason: two accounts sharing a browser cannot see
 * each other's records, so the only thing they share is a start line.
 */
export const clientsNewsSince = (): number => {
  if (since !== undefined) return since;

  try {
    const stored = window.localStorage.getItem(SINCE_KEY);
    const parsed = stored ? Number(stored) : NaN;
    if (Number.isFinite(parsed)) {
      since = parsed;
      return since;
    }

    since = Date.now();
    window.localStorage.setItem(SINCE_KEY, String(since));
  } catch {
    since = Date.now();
  }

  return since;
};

/**
 * Forgets everything, in memory and in storage — the seen ids and the
 * baseline moment both. Test-only: these are module singletons over
 * `localStorage` keys, so without this a client one test opens stays opened
 * for the next one, and a baseline one test set would quieten the next one's
 * fixtures.
 */
export const resetOpenedClients = (): void => {
  since = undefined;
  try {
    window.localStorage.removeItem(SINCE_KEY);
  } catch {
    /* nothing to undo */
  }
  store.reset();
};
